import { ref, computed, getCurrentInstance, nextTick } from 'vue';
import { useStore } from 'vuex';
import { uniqBy } from 'lodash';

export function useLessons() {
  const store = useStore();
  const instance = getCurrentInstance();
  const analytics = instance?.proxy?.$analytics;

  const loadingLessons = ref(false);

  const allLessonFlashcardStates = computed(() => {
    return store.getters['flashcardStates/lessonFlashcardStates'];
  });

  function getFilteredLesson(lesson) {
    if (!lesson) {
      return lesson;
    }

    const productAssociationIds = lesson.productAssociationIds || [];
    const flashcardIds = lesson.flashcardIds || [];
    const learningSession = lesson.learningSession || [];
    const hasProductAssociationId = store.getters['productAssociations/hasId'];

    if (!productAssociationIds.length || !hasProductAssociationId) {
      return lesson;
    }

    const keepIndexes = productAssociationIds
      .map((id, index) => hasProductAssociationId(id) ? index : null)
      .filter(index => index !== null);

    return {
      ...lesson,
      productAssociationIds: keepIndexes.map(index => productAssociationIds[index]),
      flashcardIds: keepIndexes.map(index => flashcardIds[index]),
      learningSession: keepIndexes.map(index => learningSession[index]),
    };
  }

  const lessons = computed(() => {
    const rawLessons = store.getters['lessons/lessons'] || [];

    return uniqBy(rawLessons, 'id')
      .map(lesson => getFilteredLesson(lesson))
      .filter(lesson => lesson.flashcardIds.length > 0)
      .sort((a, b) => a.order - b.order);
  });

  function status(lesson) {
    const filteredLesson = getFilteredLesson(lesson);

    const expectedFlashcardIds = filteredLesson?.flashcardIds || [];

    if (expectedFlashcardIds.length === 0) {
      return 'no-flashcards';
    }

    const completedFlashcardIds = new Set(
      allLessonFlashcardStates.value
        .filter(state => state.lessonId === lesson.id)
        .map(state => state.flashcardId),
    );

    if (completedFlashcardIds.size === 0) {
      return 'not-started';
    }

    const allFlashcardsCompleted = expectedFlashcardIds.every(flashcardId =>
      completedFlashcardIds.has(flashcardId),
    );

    return allFlashcardsCompleted ? 'completed' : 'in-progress';
  }

  function getFirstUnansweredFlashcardId(lesson) {
    if (!lesson || !lesson.flashcardIds) {
      return null;
    }

    const lessonFlashcardStates = allLessonFlashcardStates.value.filter(
      state => state.lessonId === lesson.id,
    );
    const answeredFlashcardIds = lessonFlashcardStates.map(state => state.flashcardId);
    const firstUnansweredIndex = lesson.flashcardIds.findIndex(
      flashcardId => !answeredFlashcardIds.includes(flashcardId),
    );

    return firstUnansweredIndex === -1 ? null : lesson.flashcardIds[firstUnansweredIndex];
  }

  function allLessonsCompleted() {
    if (!lessons.value || lessons.value.length === 0) {
      return false;
    }

    return lessons.value.every(lesson => status(lesson) === 'completed');
  }

  function createNewLesson() {
    return store.dispatch('lessons/createLesson').catch(() => {});
  }

  function getLessons() {
    loadingLessons.value = true;
    return store.dispatch('lessons/fetchLessons')
      .then(response => {
        if (response.data.records.length === 0) {
          createNewLesson();
        } else if (allLessonsCompleted()) {
          createNewLesson();
        }
      })
      .finally(() => {
        loadingLessons.value = false;
      });
  }

  function getLessonNumber(lesson) {
    const match = lesson?.name?.match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
  }

  function checkLessonCompletion(lessonId) {
    const lesson = lessons.value.find(lesson => lesson.id === lessonId);
    if (!lesson) return;

    nextTick(() => {
      const lessonStatus = status(lesson);
      if (lessonStatus === 'completed') {
        const lessonNumber = getLessonNumber(lesson);
        analytics?.events?.completedLesson?.(lessonNumber);
      }
    });
  }

  return {
    loadingLessons,
    allLessonFlashcardStates,
    lessons,
    getLessons,
    getFirstUnansweredFlashcardId,
    allLessonsCompleted,
    createNewLesson,
    getFilteredLesson,
    status,
    getLessonNumber,
    checkLessonCompletion,
  };
}
